import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import ChangeList from './ChangeList';
import ReviewImage from './ReviewImage';
import { colour, pillSx } from '../admin/tokens';
import { formatMoney } from '../../utils/money';

/**
 * A tab for each child.
 *
 * The review used to run every child's changes down one column, so a family with four children
 * was a long scroll in which it was easy to lose which name you were reading. A tab per child
 * keeps one child in front of the reviewer at a time, with their fees and their own card
 * pictures beside what changed about them.
 */

const FeeRow = ({ label, value, muted = false, strong = false }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: `1px solid ${colour.border}` }}>
        <Typography sx={{ fontSize: '13px', color: muted ? colour.muted : colour.bodySoft }}>{label}</Typography>
        <Typography sx={{ fontSize: '13px', fontWeight: strong ? 800 : 600, color: strong ? colour.ink : colour.body }}>
            {value}
        </Typography>
    </Box>
);

const StudentFees = ({ fees }) => {
    if (!fees) {
        return (
            <Typography sx={{ fontSize: '13px', color: colour.faint, mt: 1 }}>
                No fees recorded for this child.
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            <FeeRow label="School fees before discount" value={formatMoney(fees.beforeDiscount)} />
            {fees.siblingDiscount > 0 && (
                <FeeRow label="Sibling discount" value={`− ${formatMoney(fees.siblingDiscount)}`} muted />
            )}
            {fees.healthCareCardDiscount > 0 && (
                <FeeRow label="Health care card discount" value={`− ${formatMoney(fees.healthCareCardDiscount)}`} muted />
            )}
            {fees.discount > 0 && fees.discountType && (
                <FeeRow label={fees.discountType} value={`− ${formatMoney(fees.discount)}`} muted />
            )}
            <FeeRow label="School fees" value={formatMoney(fees.afterHealthCareCardDiscount || fees.afterSiblingDiscount)} strong />
            <FeeRow label="Resource fee" value={formatMoney(fees.resource)} />
            <FeeRow label="Deposit" value={formatMoney(fees.deposit)} />
        </Box>
    );
};

const StudentTabs = ({ students = [], images = [] }) => {
    const [active, setActive] = useState(0);

    if (!students.length) {
        return (
            <Typography sx={{ fontSize: '13px', color: colour.faint }}>
                No children recorded against this re-enrolment.
            </Typography>
        );
    }

    const student = students[Math.min(active, students.length - 1)];
    // The card pictures a parent uploaded for one child specifically, rather than the family's.
    const theirImages = images.filter(
        (image) => String(image.student_id || '') === String(student.stud_code || '')
    );

    return (
        <Box>
            <Tabs
                value={Math.min(active, students.length - 1)}
                onChange={(event, value) => setActive(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    minHeight: 40,
                    borderBottom: `1px solid ${colour.border}`,
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '13.5px',
                        minHeight: 40,
                        color: colour.muted,
                    },
                    '& .Mui-selected': { color: `${colour.primaryDeep} !important` },
                    '& .MuiTabs-indicator': { bgcolor: colour.primary, height: 3, borderRadius: '3px 3px 0 0' },
                }}
            >
                {students.map((child, index) => (
                    <Tab
                        key={child.stud_code || index}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                {child.name || child.stud_code}
                                {child.changes?.length > 0 && (
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colour.primary }} />
                                )}
                            </Box>
                        }
                    />
                ))}
            </Tabs>

            <Box sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                    {student.yearGroup != null && (
                        <Chip size="small" label={`Year ${student.yearGroup}`} sx={pillSx('neutral')} />
                    )}
                    {student.enrolled === false && (
                        <Chip size="small" label="Not returning" sx={pillSx('warn')} />
                    )}
                    {student.tassStatus && (
                        <Chip
                            size="small"
                            label={student.tassStatus === 'synced' ? 'In TASS' : 'Not yet in TASS'}
                            sx={pillSx(student.tassStatus === 'synced' ? 'good' : 'warn')}
                        />
                    )}
                </Box>

                {/* No `changes` key at all is the accounts team, who are not shown what a
                    family changed. An empty list is a child who changed nothing, which is worth
                    saying. */}
                {student.changes !== undefined && (
                    student.changes.length > 0 ? (
                        <ChangeList changes={student.changes} />
                    ) : (
                        <Typography sx={{ fontSize: '13px', color: colour.faint }}>
                            Nothing changed for this child.
                        </Typography>
                    )
                )}

                {theirImages.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: colour.body, mb: 1 }}>
                            Their own card
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {theirImages.map((image, index) => (
                                <ReviewImage key={`${image.object_path}-${index}`} image={image} />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Likewise: missing means the enrolment team, who do not work in the fees.
                    Null means a child with no fee row, which StudentFees says plainly. */}
                {student.fees !== undefined && (
                    <Box sx={{ mt: 2.5 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: colour.body, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            What this child was charged
                        </Typography>
                        <StudentFees fees={student.fees} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default StudentTabs;
